'use client';

import Image from 'next/image';
import React, { useEffect, useState } from 'react';

import {
    Align,
    Box,
    BoxContent,
    Button,
    ButtonList,
    ButtonVariant,
    Checkbox,
    DirectionEnum,
    Divider,
    FlexBox,
    FlexItem,
    GapSize,
    Icon,
    IconName,
    IconSize,
    Justify,
    Link,
    List,
    ListItem,
    Modal,
    ModalBody,
    ModalFooter,
    Text,
    TextLevels,
    TextMarkup,
    Title,
    TitleLevels,
    TrilogyColor,
    TypographyBold,
    View,
} from '@bytel/trilogy-react';

import {
    PartnerProductInfoType,
    TradeInEligibilityContext,
    getPartnerProductTradeInQuote,
    submitTradeIn,
} from '@services/trade-in';

import { EasyTradeInResult, EasyTradeInStatus } from '@app-types/easy-trade-in';
import { TradeInCriteriaResponsesType, TradeInCriteriaType } from '@app-types/trade-in';

import { InlinePrice } from '@components/inline-price';
import { Loader } from '@components/loader';

import { EASY_TRADE_IN_SUB_TYPE } from '@constants/easy-trade-in';

import recommerceIcon from '@assets/logo-recommerce.svg';
import { TradeInBonusInfo } from '@product-detail/components';

import {
    sendTradeInConfirmationTracking,
    sendTradeInStep1Tracking,
    sendTradeInStep2Tracking,
    sendTradeInStep3Tracking,
} from '../../_helpers/tracking';
import { EasyTradeInConfirmedContent } from '../components';
import { TradeInQuestionnaire } from '../mobile-trade-in/components';

enum EasyTradeInModalStep {
    PHONE_INFO = 1,
    QUESTIONNAIRE = 2,
    RESULT = 3,
}

function getCreditRestantApresReprise(result: EasyTradeInResult) {
    if (result.status === EasyTradeInStatus.TOO_LOW) {
        return undefined;
    }

    if (result.status === EasyTradeInStatus.SETTLED) {
        return result.remainingCreditTerminal;
    }

    return result.creditBeforeTradeIn;
}

export type EasyTradeInModalProps = {
    isOpen: boolean;
    onClose: () => void;
    partnerProductId?: string;
    creditId: string | undefined;
    context: TradeInEligibilityContext;
    onConfirm: (result: EasyTradeInResult) => void;
    onKeepPhone: (result: EasyTradeInResult) => void;
    tradeInBonus?: number;
    partnerProduct: PartnerProductInfoType | undefined;
    isLoadingProduct: boolean;
};

function PhoneInfoStep({
    phoneLabel,
    isLoading,
    onNext,
    onClose,
    tradeInBonus,
    isOpen,
}: Readonly<{
    phoneLabel: string;
    isLoading: boolean;
    onNext: () => void;
    onClose: () => void;
    tradeInBonus?: number;
    isOpen: boolean;
}>) {
    useEffect(() => {
        if (isOpen) {
            sendTradeInStep1Tracking(true, true);
        }
    }, [isOpen]);

    return (
        <>
            <ModalBody>
                {isLoading ? (
                    <View flexable justify={Justify.CENTER}>
                        <Loader />
                    </View>
                ) : (
                    <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.FIVE}>
                        <Text marginless level={TextLevels.TWO} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                            Votre téléphone actuel à rendre&nbsp;:
                        </Text>
                        <Title marginless level={TitleLevels.FIVE}>
                            {phoneLabel}
                        </Title>
                        <Text marginless level={TextLevels.TWO}>
                            Cette reprise concerne la clôture du crédit restant sur votre téléphone uniquement (hors
                            accessoires).
                        </Text>
                        <Box shadowless>
                            <BoxContent backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                                <Text level={TextLevels.TWO} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                    Comment ça marche&nbsp;?
                                </Text>
                                <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.FOUR}>
                                    <FlexBox direction={DirectionEnum.ROW} align={Align.CENTER} gap={2}>
                                        <FlexItem narrow>
                                            <Icon name={IconName.MOBILE_EYE} size={IconSize.SMALL} />
                                        </FlexItem>
                                        <FlexItem>
                                            <Text level={TextLevels.TWO} marginless>
                                                Estimez maintenant l&apos;état de votre téléphone
                                            </Text>
                                        </FlexItem>
                                    </FlexBox>
                                    <FlexBox direction={DirectionEnum.ROW} align={Align.CENTER} gap={2}>
                                        <FlexItem narrow>
                                            <Icon name={IconName.PARCEL} size={IconSize.SMALL} />
                                        </FlexItem>
                                        <FlexItem>
                                            <Text level={TextLevels.TWO} marginless>
                                                Recevez une enveloppe préaffranchie et préadressée à notre partenaire
                                                <div>
                                                    <Image src={recommerceIcon} alt='recommerce' width={100} />
                                                </div>
                                            </Text>
                                        </FlexItem>
                                    </FlexBox>
                                    <FlexBox direction={DirectionEnum.ROW} align={Align.CENTER} gap={2}>
                                        <FlexItem narrow>
                                            <Icon name={IconName.TRUCK_SPEED} size={IconSize.SMALL} />
                                        </FlexItem>
                                        <FlexItem>
                                            <Text level={TextLevels.TWO} marginless>
                                                Renvoyez gratuitement votre téléphone pour une vérification de son état
                                                en atelier
                                            </Text>
                                        </FlexItem>
                                    </FlexBox>
                                    <FlexBox direction={DirectionEnum.ROW} align={Align.CENTER} gap={2}>
                                        <FlexItem narrow>
                                            <Icon name={IconName.CREDIT_CARD_ACCEPTED} size={IconSize.SMALL} />
                                        </FlexItem>
                                        <FlexItem>
                                            <Text level={TextLevels.TWO} marginless>
                                                Selon son état réel, on clôture sans frais tout ou partie du crédit de
                                                votre téléphone
                                            </Text>
                                        </FlexItem>
                                    </FlexBox>
                                </FlexBox>
                            </BoxContent>
                        </Box>
                        {(tradeInBonus ?? 0) > 0 && (
                            <Text level={TextLevels.TWO} marginless>
                                Et vous pouvez bénéficier d&apos;un{' '}
                                <Text markup={TextMarkup.SPAN} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                                    bonus reprise de <InlinePrice level={TextLevels.TWO} amount={tradeInBonus} />.
                                </Text>{' '}
                                Alors, on y va&nbsp;?
                            </Text>
                        )}
                    </FlexBox>
                )}
            </ModalBody>
            <ModalFooter>
                <ButtonList>
                    <Button variant={ButtonVariant.GHOST} onClick={onClose}>
                        Fermer
                    </Button>
                    <Button variant={ButtonVariant.PRIMARY} disabled={isLoading} onClick={onNext}>
                        Commencez l&apos;estimation
                    </Button>
                </ButtonList>
            </ModalFooter>
        </>
    );
}

function QuestionnaireStep({
    phoneLabel,
    questions,
    isLoading,
    isComplete,
    isSubmitting,
    onResponsesChange,
    onSubmit,
    onBack,
    onClose,
}: Readonly<{
    phoneLabel?: string;
    questions: TradeInCriteriaType;
    isLoading: boolean;
    isComplete: boolean;
    isSubmitting: boolean;
    onResponsesChange: (responses: TradeInCriteriaResponsesType['questions']) => void;
    onSubmit: () => void;
    onBack: () => void;
    onClose: () => void;
}>) {
    useEffect(() => {
        sendTradeInStep2Tracking(true, phoneLabel ?? '', true);
    }, [phoneLabel]);
    return (
        <>
            <ModalBody>
                <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.FIVE}>
                    <Link role='button' onClick={onBack} className='is-flex is-aligned-center is-marginless'>
                        <Icon name={IconName.ARROW_LEFT} />
                        &nbsp;Retour à l&apos;étape précédente
                    </Link>
                    <Text typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD} level={TextLevels.ONE} marginless>
                        Indiquez l&apos;état de votre {phoneLabel}
                    </Text>
                    {isLoading ? (
                        <View flexable justify={Justify.CENTER}>
                            <Loader />
                        </View>
                    ) : (
                        <TradeInQuestionnaire questions={questions} onResponsesChange={onResponsesChange} />
                    )}
                </FlexBox>
            </ModalBody>
            <ModalFooter>
                <ButtonList>
                    <Button variant={ButtonVariant.SECONDARY} onClick={onClose}>
                        Fermer
                    </Button>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        disabled={!isComplete}
                        loading={isSubmitting}
                        onClick={onSubmit}
                    >
                        Étape suivante
                    </Button>
                </ButtonList>
            </ModalFooter>
        </>
    );
}

function ResultStep({
    result,
    onConfirm: _onConfirm,
    onKeepPhone,
    onClose,
    tradeInBonus,
    phoneLabel,
}: Readonly<{
    result: EasyTradeInResult;
    onConfirm: () => void;
    onKeepPhone: () => void;
    onClose: () => void;
    tradeInBonus?: number;
    phoneLabel: string;
}>) {
    const [hasAccepted, setHasAccepted] = useState<boolean>(false);
    const onConfirm = () => {
        _onConfirm();
        sendTradeInConfirmationTracking(
            true,
            phoneLabel,
            Math.abs(result.estimatedValue),
            true,
            result.status !== EasyTradeInStatus.TOO_LOW,
            getCreditRestantApresReprise(result),
        );
    };
    useEffect(() => {
        sendTradeInStep3Tracking(
            true,
            phoneLabel,
            Math.abs(result.estimatedValue),
            true,
            result.status !== EasyTradeInStatus.TOO_LOW,
            getCreditRestantApresReprise(result),
        );
    }, [phoneLabel, result]);

    if (result.status === EasyTradeInStatus.TOO_LOW) {
        return (
            <>
                <ModalBody>
                    <EasyTradeInConfirmedContent result={result} />
                    <TooLowModalFooter />
                </ModalBody>
                <ModalFooter>
                    <ButtonList>
                        <Button variant={ButtonVariant.SECONDARY} onClick={onClose}>
                            Fermer
                        </Button>
                        <Button variant={ButtonVariant.PRIMARY} onClick={onKeepPhone}>
                            Conserver mon téléphone
                        </Button>
                    </ButtonList>
                </ModalFooter>
            </>
        );
    }

    if (result.status === EasyTradeInStatus.PARTIAL) {
        return (
            <>
                <ModalBody>
                    <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.FOUR}>
                        <EasyTradeInConfirmedContent result={result} />
                        <TradeInBonusInfo tradeInBonus={tradeInBonus} />
                    </FlexBox>
                    <ModalSharedFooterContent
                        hasAccepted={hasAccepted}
                        setHasAccepted={setHasAccepted}
                        additionalInformation='Si son état ne correspond pas à celui déclaré, notre partenaire re!commerce reviendra vers vous.'
                    />
                </ModalBody>
                <ModalFooter>
                    <ButtonList>
                        <Button variant={ButtonVariant.SECONDARY} onClick={onClose}>
                            Fermer
                        </Button>
                        <Button disabled={!hasAccepted} variant={ButtonVariant.PRIMARY} onClick={onConfirm}>
                            Confirmer votre reprise
                        </Button>
                    </ButtonList>
                </ModalFooter>
            </>
        );
    }

    return (
        <>
            <ModalBody>
                <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.FOUR}>
                    <EasyTradeInConfirmedContent result={result} />
                    <TradeInBonusInfo tradeInBonus={tradeInBonus} />
                </FlexBox>
                <ModalSharedFooterContent
                    hasAccepted={hasAccepted}
                    setHasAccepted={setHasAccepted}
                    additionalInformation='Si son état
                correspond à celui déclaré, on clôture votre crédit sans frais.'
                />
            </ModalBody>
            <ModalFooter>
                <ButtonList>
                    <Button variant={ButtonVariant.SECONDARY} onClick={onClose}>
                        Fermer
                    </Button>
                    <Button disabled={!hasAccepted} variant={ButtonVariant.PRIMARY} onClick={onConfirm}>
                        Finaliser la reprise
                    </Button>
                </ButtonList>
            </ModalFooter>
        </>
    );
}

function ModalSharedFooterContent({
    hasAccepted,
    setHasAccepted,
    additionalInformation,
}: Readonly<{
    hasAccepted: boolean;
    setHasAccepted: (value: boolean) => void;
    additionalInformation: string;
}>) {
    return (
        <>
            <Divider />
            <Title level={TitleLevels.SIX}>Vous commandez votre nouveau téléphone, et ensuite...</Title>
            <Text level={TextLevels.TWO}>
                Vous recevrez une enveloppe préaffranchie pour une vérification de votre téléphone.{' '}
                {additionalInformation}
            </Text>
            <Divider />
            <View flexable className='is-justified-start-mobile is-justified-end-tablet'>
                <Checkbox checked={hasAccepted} onChange={() => setHasAccepted(!hasAccepted)}>
                    <Text>
                        <Text markup={TextMarkup.SPAN}>J&apos;accepte les&nbsp;</Text>
                        <Text markup={TextMarkup.SPAN}>
                            <Link
                                href='https://assets.recommerce.com/buyback-webgen-app/bouyguestelecom/fr/CGA.Bouygues-Recommerce.pdf'
                                blank
                            >
                                Conditions Générales de Reprise
                            </Link>
                        </Text>
                    </Text>
                </Checkbox>
            </View>
        </>
    );
}

export function EasyTradeInModal({
    isOpen,
    onClose,
    partnerProduct,
    isLoadingProduct,
    partnerProductId,
    context,
    onConfirm,
    onKeepPhone,
    creditId,
    tradeInBonus,
}: Readonly<EasyTradeInModalProps>) {
    const [step, setStep] = useState<EasyTradeInModalStep>(EasyTradeInModalStep.PHONE_INFO);
    const [responses, setResponses] = useState<TradeInCriteriaResponsesType['questions']>([]);
    const [result, setResult] = useState<EasyTradeInResult | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const phoneInfo = partnerProduct?.phoneInfo;
    const phoneLabel = phoneInfo ? `${phoneInfo.brand} ${phoneInfo.name}` : 'téléphone';
    const questions = partnerProduct?.questions ?? [];

    useEffect(() => {
        if (!isOpen) {
            return;
        }
        setStep(EasyTradeInModalStep.PHONE_INFO);
        setResponses([]);
        setResult(null);
    }, [isOpen]);

    const isQuestionnaireComplete = questions.length > 0 && responses.length === questions.length;

    const handleSubmitQuestionnaire = async () => {
        if (!partnerProductId || !creditId) {
            return;
        }
        setIsSubmitting(true);
        try {
            const quote = await getPartnerProductTradeInQuote(
                partnerProductId,
                {
                    mode: 'GARANTIE',
                    subscribedFundingId: creditId,
                    criteria: responses,
                },
                context.user ?? null,
            );
            const conformity = await submitTradeIn(context, quote.id, EASY_TRADE_IN_SUB_TYPE);
            if (conformity) {
                setResult({ ...conformity, partnerRequestId: quote.id });
                setStep(EasyTradeInModalStep.RESULT);
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Modal active={isOpen} onClose={onClose} title='Reprise mobile Smart Change'>
            {step === EasyTradeInModalStep.PHONE_INFO && (
                <PhoneInfoStep
                    phoneLabel={phoneLabel}
                    isLoading={isLoadingProduct}
                    onNext={() => setStep(EasyTradeInModalStep.QUESTIONNAIRE)}
                    onClose={onClose}
                    tradeInBonus={tradeInBonus}
                    isOpen={isOpen}
                />
            )}
            {step === EasyTradeInModalStep.QUESTIONNAIRE && (
                <QuestionnaireStep
                    phoneLabel={phoneLabel}
                    questions={questions}
                    isLoading={isLoadingProduct}
                    isComplete={isQuestionnaireComplete}
                    isSubmitting={isSubmitting}
                    onResponsesChange={setResponses}
                    onSubmit={handleSubmitQuestionnaire}
                    onBack={() => setStep(EasyTradeInModalStep.PHONE_INFO)}
                    onClose={onClose}
                />
            )}
            {step === EasyTradeInModalStep.RESULT && result && (
                <ResultStep
                    phoneLabel={phoneLabel}
                    result={result}
                    onConfirm={() => onConfirm(result)}
                    onKeepPhone={() => onKeepPhone(result)}
                    onClose={onClose}
                    tradeInBonus={tradeInBonus}
                />
            )}
        </Modal>
    );
}

function TooLowModalFooter() {
    return (
        <>
            <Divider />
            <Text level={TextLevels.ONE} typo={TypographyBold.TEXT_WEIGHT_SEMIBOLD}>
                Ces 2 solutions s&apos;offrent à vous&nbsp;:
            </Text>
            <List>
                <ListItem>
                    <Text level={TextLevels.TWO}>
                        Vous avez assuré votre téléphone&nbsp;? Contactez SPB pour la prise en charge de votre
                        téléphone&nbsp;: 09&nbsp;70&nbsp;83&nbsp;03&nbsp;06.
                    </Text>
                </ListItem>
                <ListItem>
                    <Text level={TextLevels.TWO}>
                        Vous bénéficiez d&apos;une remise de 30% sur votre réparation chez notre partenaire WeFix.
                    </Text>
                </ListItem>
            </List>
        </>
    );
}
